/**
 * Wing builder — two wing halves as tapered boxes with sweep and dihedral.
 *
 * Wings extend along X axis (left/right), positioned along Z based on wingPositionRatio.
 */

import * as THREE from 'three'
import type { CategoryProfile } from '../types'

export function createWings(
  wingspan: number,
  chord: number,
  thickness: number,
  yPosition: number,
  zPosition: number,
  profile: CategoryProfile,
  material: THREE.MeshStandardMaterial
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'wings'

  const halfSpan = wingspan / 2
  const sweepRad = (profile.wingSweepDeg * Math.PI) / 180
  const dihedralRad = (profile.wingDihedralDeg * Math.PI) / 180

  // Sweep offset: how far back the wingtip is relative to root
  const sweepOffset = Math.tan(sweepRad) * halfSpan

  for (const side of [-1, 1] as const) {
    const wingGeo = new THREE.BoxGeometry(halfSpan, thickness, chord)
    const wing = new THREE.Mesh(wingGeo, material)

    // Position center of wing half at mid-span
    wing.position.set(
      side * (halfSpan / 2),
      yPosition,
      zPosition - sweepOffset / 2 // average sweep offset for center of wing half
    )

    // Dihedral: rotate around Z axis so wingtips are higher
    wing.rotation.z = side * -dihedralRad

    wing.castShadow = true
    wing.receiveShadow = true
    group.add(wing)
  }

  return group
}
